import {useState} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {fetchWmsLayers} from "base/map/DynamicLayerUtils";

const WmsLayerDialog = ({open, onClose, onAdd}) => {
    const [url, setUrl] = useState("");
    const [name, setName] = useState("WMS Externo");
    const [availableLayers, setAvailableLayers] = useState([]);
    const [selectedLayers, setSelectedLayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFetchLayers = async () => {
        setLoading(true);
        setError(null);
        try {
            const layers = await fetchWmsLayers(url);
            setAvailableLayers(layers);
        } catch (err) {
            setError("Erro ao obter as capacidades do WMS. Revisa a URL.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = layerName => () => {
        const currentIndex = selectedLayers.indexOf(layerName);
        const newSelectedLayers = [...selectedLayers];

        if (currentIndex === -1) {
            newSelectedLayers.push(layerName);
        } else {
            newSelectedLayers.splice(currentIndex, 1);
        }

        setSelectedLayers(newSelectedLayers);
    };

    const handleAdd = () => {
        onAdd(url, selectedLayers.join(","), name);
        handleClose();
    };

    const handleClose = () => {
        onClose();
        // Reset state after a delay to avoid flickering during close animation
        setTimeout(() => {
            setUrl("");
            setName("WMS Externo");
            setAvailableLayers([]);
            setSelectedLayers([]);
            setError(null);
        }, 300);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Engadir Capa WMS</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <TextField
                        label="URL do servizo WMS"
                        fullWidth
                        value={url}
                        onChange={e => setUrl(e.target.value)}
                        placeholder="https://ejemplo.com/wms"
                    />
                    <Button
                        variant="contained"
                        onClick={handleFetchLayers}
                        disabled={!url || loading}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Consultar Capas"
                        )}
                    </Button>
                    {error && (
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    )}
                    {availableLayers.length > 0 && (
                        <>
                            <TextField
                                label="Nome da agrupación"
                                fullWidth
                                value={name}
                                onChange={e => setName(e.target.value)}
                                helperText="Nome que se mostrará no menú de capas"
                            />
                            <Typography variant="subtitle1" sx={{mt: 1}}>
                                Selecciona as capas a engadir:
                            </Typography>
                            <List
                                sx={{
                                    maxHeight: 300,
                                    overflow: "auto",
                                    border: "1px solid lightgrey",
                                    borderRadius: 1,
                                }}
                            >
                                {availableLayers.map(layer => (
                                    <ListItem key={layer.name} disablePadding>
                                        <ListItemButton
                                            onClick={handleToggle(layer.name)}
                                            dense
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={
                                                        selectedLayers.indexOf(layer.name) !== -1
                                                    }
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={layer.title}
                                                secondary={layer.name}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button
                    onClick={handleAdd}
                    variant="contained"
                    disabled={selectedLayers.length === 0}
                >
                    Engadir
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WmsLayerDialog;
